<script lang="ts">
  import type { Snippet } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { backOut, cubicIn } from "svelte/easing";

  /**
   * Modales Sheet: auf dem Handy von unten (wegwischbar), auf dem iPad zentriert.
   * Fokus wird gefangen und beim Schließen zurückgegeben; Escape schließt.
   */
  let {
    title,
    onclose,
    children,
    width = 460,
  }: { title: string; onclose: () => void; children: Snippet; width?: number } = $props();

  let panel: HTMLDivElement | undefined = $state();
  let dragY = $state(0);
  let dragging = $state(false);
  let startY = 0;
  const previouslyFocused = document.activeElement as HTMLElement | null;

  $effect(() => {
    const first = panel?.querySelector<HTMLElement>("button, input, select, textarea, [href]");
    first?.focus({ preventScroll: true });
    return () => previouslyFocused?.focus?.({ preventScroll: true });
  });

  function onkeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      onclose();
      return;
    }
    if (event.key !== "Tab" || !panel) return;
    const focusable = [
      ...panel.querySelectorAll<HTMLElement>("button:not(:disabled), input, select, textarea, [href]"),
    ];
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function grabStart(event: PointerEvent): void {
    dragging = true;
    startY = event.clientY;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }
  function grabMove(event: PointerEvent): void {
    if (dragging) dragY = Math.max(0, event.clientY - startY);
  }
  function grabEnd(): void {
    if (!dragging) return;
    dragging = false;
    if (dragY > 110) onclose();
    else dragY = 0;
  }
</script>

<div class="backdrop" transition:fade={{ duration: 200 }} onclick={onclose} role="presentation">
  <div
    bind:this={panel}
    class="panel"
    style:--w="{width}px"
    style:transform={dragY ? `translateY(${dragY}px)` : null}
    class:dragging
    role="dialog"
    aria-modal="true"
    aria-label={title}
    tabindex="-1"
    onclick={(e) => e.stopPropagation()}
    {onkeydown}
    in:fly={{ y: 80, duration: 450, easing: backOut }}
    out:fly={{ y: 60, duration: 200, easing: cubicIn }}
  >
    <div
      class="grab"
      onpointerdown={grabStart}
      onpointermove={grabMove}
      onpointerup={grabEnd}
      onpointercancel={grabEnd}
      role="presentation"
    >
      <span></span>
    </div>
    {@render children()}
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: var(--z-sheet);
  }
  .panel {
    width: min(100%, var(--w));
    max-height: min(86vh, 760px);
    overflow-y: auto;
    border-radius: 24px 24px 0 0;
    background: #1c1c21;
    box-shadow: var(--shadow-2);
    padding: 6px 12px calc(16px + var(--safe-bottom));
    transition: transform var(--dur-base) var(--ease-spring);
    scrollbar-width: none;
  }
  .panel.dragging {
    transition: none;
  }
  .grab {
    display: flex;
    justify-content: center;
    padding: 6px 0 10px;
    touch-action: none;
    cursor: grab;
  }
  .grab span {
    width: 40px;
    height: 5px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.2);
  }
  @media (min-width: 900px) {
    .backdrop {
      align-items: center;
    }
    .panel {
      border-radius: var(--r-xl);
      padding-bottom: 16px;
    }
  }
</style>
